import React, { useState } from 'react'
import { supabase } from '../supabase'

const LEGAL_URL = 'https://gist.github.com/ananjan-nandi-9/a138c0a48722f9df079e3e063be6cf2d'
const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/nowpilot-ai-copilot-for-f/fbcdieoakcimbcaeblflmolklimhlnii'

declare global {
  interface Window {
    gtag: (...args: any[]) => void
  }
}

function FooterSignup() {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const { error } = await supabase
        .from('email_subscribers')
        .insert([{ email }])
        .select()

      if (error) {
        if (error.code === '23505') {
          setMessage('You’re already subscribed.')
        } else {
          setMessage('Something went wrong. Please try again.')
        }
        console.error('Supabase Error:', error)
      } else {
        if (typeof window.gtag !== 'undefined') {
          window.gtag('event', 'newsletter_signup', {
            event_category: 'conversion',
            event_label: 'footer_signup',
            value: 1,
          })
        }

        try {
          const response = await fetch('/.netlify/functions/send-welcome-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          })

          if (response.ok) {
            setMessage('Thanks — check your inbox.')
          } else {
            const error = await response.text()
            console.error('Email function error:', error)
            setMessage('Subscribed. Welcome email may be delayed.')
          }
        } catch (emailError) {
          console.error('Email send error:', emailError)
          setMessage('Subscribed. Welcome email may be delayed.')
        }

        setEmail('')
      }
    } catch (error) {
      setMessage('Something went wrong. Please try again.')
      console.error('General Error:', error)
    } finally {
      setIsSubmitting(false)
      setTimeout(() => setMessage(''), 7000)
    }
  }

  return (
    <div>
      <h4 className="eyebrow mb-4">Stay in the loop</h4>
      <p className="text-sm text-ink-mute mb-3 leading-relaxed">
        Occasional updates as we build. No spam.
      </p>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="email"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-ink/15 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
          required
          disabled={isSubmitting}
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-ink text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-ink/90 transition-colors disabled:opacity-50 whitespace-nowrap"
        >
          {isSubmitting ? '…' : 'Subscribe'}
        </button>
      </form>
      {message && (
        <p
          className={`mt-2 text-xs ${
            message.includes('Thanks') ||
            message.includes('Subscribed') ||
            message.includes('already')
              ? 'text-green-700'
              : 'text-red-700'
          }`}
        >
          {message}
        </p>
      )}
    </div>
  )
}

export function Footer() {
  return (
    <footer className="bg-paper border-t border-ink/10">
      <div className="container mx-auto px-4 max-w-6xl py-14">
        <div className="grid gap-10 md:grid-cols-4">
          <div className="md:col-span-1">
            <a href="#top" className="flex items-center gap-2 mb-4">
              <img
                src="https://iyuecznfxavbpwsapptp.supabase.co/storage/v1/object/public/images/NowPilot-riposte-logo/np-riposte-logo-full-color.svg"
                alt=""
                aria-hidden
                className="h-6 w-auto"
              />
              <span className="font-display text-lg text-ink tracking-tight">
                NowPilot
              </span>
            </a>
            <p className="font-display text-base text-ink-mute leading-relaxed italic">
              An AI copilot for focus, built for ADHD brains.
            </p>
          </div>

          <div>
            <h4 className="eyebrow mb-4">Product</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href={CHROME_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-ink-mute hover:text-ink transition-colors"
                >
                  Chrome extension
                </a>
              </li>
              <li>
                <a
                  href="https://nowpilot.ai/web"
                  className="text-ink-mute hover:text-ink transition-colors"
                >
                  Web app
                </a>
              </li>
              <li>
                <a
                  href="https://nowpilot.ai/downloads/mac"
                  className="text-ink-mute hover:text-ink transition-colors"
                >
                  Mac
                </a>
              </li>
              <li>
                <a
                  href="https://nowpilot.ai/downloads/win"
                  className="text-ink-mute hover:text-ink transition-colors"
                >
                  Windows
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="eyebrow mb-4">Company</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href="#team"
                  className="text-ink-mute hover:text-ink transition-colors"
                >
                  Team
                </a>
              </li>
              <li>
                <a
                  href="mailto:team@nowpilot.ai"
                  className="text-ink-mute hover:text-ink transition-colors"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>

          <FooterSignup />
        </div>

        <div className="mt-12 pt-6 border-t border-ink/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-ink-soft">
          <span>© {new Date().getFullYear()} NowPilot. All rights reserved.</span>
          <div className="flex items-center gap-5">
            <a
              href={LEGAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-ink transition-colors"
            >
              Privacy
            </a>
            <a
              href={LEGAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-ink transition-colors"
            >
              Terms
            </a>
            <a
              href="mailto:team@nowpilot.ai"
              className="hover:text-ink transition-colors"
            >
              team@nowpilot.ai
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
